import type { LCPMetricWithAttribution } from 'web-vitals/attribution'

import type { PageType } from '@/types/nav-types'

import { EventName, EventType, type TrackedEvent } from './events'

export interface LcpPageContext {
  path: string
  url: string
  host: string
  pageType: PageType
  requestId: string
  sessionId: string
  windowId: string
  viewportWidth: number
  viewportHeight: number
}

// Stable, code-owned labels, never page text, arbitrary IDs, or form values.
export const lcpTarget = (node: Node | null): string => {
  if (!(node instanceof Element)) {
    return 'unknown'
  }
  const origin = node.closest('[data-origin]')?.getAttribute('data-origin')
  const component = node.closest('[data-component]')?.getAttribute('data-component')
  return [node.tagName.toLowerCase(), origin || component].filter(Boolean).join(':').slice(0, 160)
}

const resourceUrl = (value?: string): string | undefined => {
  if (!value) {
    return undefined
  }
  try {
    const url = new URL(value)
    return /^https?:$/.test(url.protocol) ? `${url.origin}${url.pathname}` : undefined
  } catch {
    return undefined
  }
}

export const buildLcpEvent = (metric: LCPMetricWithAttribution, page: LcpPageContext): TrackedEvent => ({
  event: EventName.WebVital,
  eventType: EventType.Performance,
  timestamp: Date.now(),
  properties: {
    metric: metric.name,
    metricId: metric.id,
    value: Math.round(metric.value),
    rating: metric.rating,
    navigationType: metric.navigationType,
    buildSha: process.env.NEXT_PUBLIC_BUILD_SHA ?? 'dev',
    path: page.path,
    pageType: page.pageType,
    requestId: page.requestId,
    $current_url: page.url,
    $host: page.host,
    $session_id: page.sessionId,
    $window_id: page.windowId,
    viewportWidth: page.viewportWidth,
    viewportHeight: page.viewportHeight,
    target: metric.attribution.target?.startsWith('#') ? 'removed-element' : metric.attribution.target,
    resourceUrl: resourceUrl(metric.attribution.url),
    timeToFirstByte: Math.round(metric.attribution.timeToFirstByte),
    resourceLoadDelay: Math.round(metric.attribution.resourceLoadDelay),
    resourceLoadDuration: Math.round(metric.attribution.resourceLoadDuration),
    elementRenderDelay: Math.round(metric.attribution.elementRenderDelay),
  },
})

type LcpListener = (metric: LCPMetricWithAttribution) => void
const listeners = new Set<LcpListener>()
let started = false

// web-vitals has document-lifetime observers, not a React cleanup API. Register
// once, and separately unsubscribe consumers so Strict Mode/remounts cannot
// double-report. Buffered entries retain LCP candidates painted before hydration.
export const captureLcp = (listener: LcpListener): (() => void) => {
  listeners.add(listener)
  if (!started && typeof PerformanceObserver !== 'undefined') {
    started = true
    void import('web-vitals/attribution')
      .then(({ onLCP }) => {
        onLCP((metric) => listeners.forEach((report) => report(metric)), { generateTarget: lcpTarget })
      })
      .catch(() => {
        started = false
      })
  }
  return () => {
    listeners.delete(listener)
  }
}
